import React from 'react';
import { SettingsToggle } from './components/SettingsToggle';

// Slider UI is 0-100; canonical UserPrefs is 0-1. Convert at the boundary.
export const toSliderUnits = (v: number) => Math.round(v * 100);
export const fromSliderUnits = (v: number) => Math.max(0, Math.min(1, v / 100));

export interface AudioTabProps {
  masterVol: number;
  musicVol: number;
  sfxVol: number;
  muted: boolean;
  setMasterVol: (v: number) => void;
  setMusicVol: (v: number) => void;
  setSfxVol: (v: number) => void;
  setMuted: (v: boolean) => void;
}

/** Audio stays a draft until the existing Apply Settings action commits it. */
export const AudioTab: React.FC<AudioTabProps> = ({
  masterVol,
  musicVol,
  sfxVol,
  muted,
  setMasterVol,
  setMusicVol,
  setSfxVol,
  setMuted,
}) => {
  const prefix = React.useId();
  const controls = [
    { key: 'master', label: 'Master Volume', hint: 'Overall game loudness.', value: masterVol, change: setMasterVol },
    { key: 'music', label: 'Music Volume', hint: 'Background music.', value: musicVol, change: setMusicVol },
    { key: 'sfx', label: 'SFX Volume', hint: 'Combat, movement and interaction sounds.', value: sfxVol, change: setSfxVol },
  ];

  return (
    <section className="settings-section" aria-labelledby={`${prefix}-volume-heading`}>
      <h3 className="settings-section-title" id={`${prefix}-volume-heading`}>Volume Levels</h3>
      <p className="settings-hint">Tune each sound channel. Audio changes are saved with Apply Settings.</p>

      <div className="settings-card">
        <div className="settings-card-row">
          <div>
            <label htmlFor={`${prefix}-muted`} className="settings-card-label" style={{ cursor: 'pointer' }}>
              Mute All Audio
            </label>
            <div className="settings-hint" id={`${prefix}-muted-hint`} style={{ margin: 0 }}>
              Silence all master, music, and sound effect channels.
            </div>
          </div>
          <SettingsToggle
            id={`${prefix}-muted`}
            checked={muted}
            onChange={setMuted}
            data-testid="settings-audio-muted"
            aria-label="Mute All Audio"
          />
        </div>
      </div>

      {controls.map((control) => (
        <div className="settings-volume" key={control.key} style={{ opacity: muted ? 0.55 : 1 }}>
          <div>
            <label htmlFor={`${prefix}-${control.key}`}>{control.label}</label>
            <output htmlFor={`${prefix}-${control.key}`}>{control.value}%</output>
          </div>
          <p className="settings-hint" id={`${prefix}-${control.key}-hint`}>{control.hint}</p>
          <input
            id={`${prefix}-${control.key}`}
            aria-describedby={`${prefix}-${control.key}-hint`}
            type="range"
            min={0}
            max={100}
            step={1}
            disabled={muted}
            value={control.value}
            onChange={(event) => control.change(Number(event.target.value))}
          />
        </div>
      ))}
    </section>
  );
};
